import { useState } from "react";
import { FaSearch } from "react-icons/fa";
import type { CCAMSearchFilters } from "cadendar-shared";

export interface CCAMSearchBarProps {
  onSearchChange: (query: string) => void;
  filters?: CCAMSearchFilters;
  onFiltersChange?: (filters: CCAMSearchFilters) => void;
  isLoading?: boolean;
}

/**
 * Barre de recherche CCAM avec debounce
 * Story 2.4: Recherche instantanée avec 300ms debounce
 */
export const CCAMSearchBar = ({
  onSearchChange,
  filters,
  onFiltersChange,
  isLoading = false,
}: CCAMSearchBarProps) => {
  const [inputValue, setInputValue] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    onSearchChange(value);
  };

  return (
    <div className="space-y-4 mb-6">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <FaSearch className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Rechercher un acte CCAM (code ou libellé)..."
          className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          role="searchbox"
          aria-label="Rechercher un acte CCAM"
          aria-busy={isLoading}
        />
        {isLoading && (
          <div
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            data-testid="loading-spinner"
          >
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        )}
      </div>

      {/* Filters (Optional - simplified for Story 2.4) */}
      {onFiltersChange && (
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters?.excludeExpired !== false}
              onChange={(e) =>
                onFiltersChange({
                  ...filters,
                  excludeExpired: e.target.checked,
                })
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Exclure les actes périmés
            </span>
          </label>
        </div>
      )}
    </div>
  );
};
